import { useEffect, useCallback } from 'react'
import type { GeoCoords } from '@/lib/utils/location'

interface MapDialogProps {
  coords: GeoCoords
  onClose: () => void
}

export function MapDialog({ coords, onClose }: MapDialogProps) {
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') onClose()
  }, [onClose])

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [handleKeyDown])

  const staticMapUrl = `/api/static-map?lat=${coords.lat}&lng=${coords.lng}&zoom=15`
  const fullMapUrl = `https://www.openstreetmap.org/?mlat=${coords.lat}&mlon=${coords.lng}#map=16/${coords.lat}/${coords.lng}`

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm animate-fade-in" onClick={onClose}>
      <div
        className="w-full max-w-2xl bg-[rgb(var(--np-surface))] border border-[rgb(var(--np-border))] rounded-lg overflow-hidden animate-slide-up"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-[rgb(var(--np-border))]">
          <div className="flex items-center gap-2 min-w-0">
            <svg className="shrink-0 text-[rgb(var(--np-accent))]" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="2" y1="12" x2="22" y2="12" />
              <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
            </svg>
            <span className="text-sm font-semibold text-[rgb(var(--np-tx-primary))] truncate">
              {coords.label ?? 'Location'}
            </span>
            <span className="text-xs font-data text-[rgb(var(--np-tx-muted))] shrink-0">
              {coords.lat.toFixed(5)}, {coords.lng.toFixed(5)}
            </span>
          </div>
          <button onClick={onClose} className="shrink-0 p-1 text-[rgb(var(--np-tx-muted))] hover:text-[rgb(var(--np-tx-primary))] transition-colors">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
        <a href={fullMapUrl} target="_blank" rel="noopener noreferrer" className="block">
          <img
            src={staticMapUrl}
            alt="Map"
            className="w-full h-80 sm:h-96 object-cover bg-[rgb(var(--np-raised))]"
          />
        </a>
        <div className="px-4 py-2 border-t border-[rgb(var(--np-border))] flex items-center justify-between">
          <a
            href={fullMapUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-[rgb(var(--np-accent))] hover:underline"
          >
            Open in OpenStreetMap
          </a>
          <a
            href={`https://www.google.com/maps?q=${coords.lat},${coords.lng}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-[rgb(var(--np-tx-muted))] hover:text-[rgb(var(--np-tx-primary))] transition-colors"
          >
            Google Maps
          </a>
        </div>
      </div>
    </div>
  )
}
